import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { analyzeThemeFonts } from '@repo/checkout/lib/analyzeThemeFonts';
import { generateGoogleFontsUrl, filterGoogleFonts } from '@repo/checkout/lib/fontUtils';
import { getFontFamilies } from '@repo/checkout/components/CheckoutCSS';

const ALL_WEIGHTS = [100, 200, 300, 400, 500, 600, 700, 800, 900];

/**
 * Check if the required font families are already loaded in the browser
 * (e.g. cached from the font picker dropdown).
 */
const areFontsLoaded = tokens => {
  if (!tokens || typeof document === 'undefined') return true;

  const mainFont = tokens.fontMain?.family;
  const headingFont = tokens.fontHeading?.family || mainFont;
  const bodyFont = tokens.fontBody?.family || mainFont;

  const specs = [];
  if (headingFont) specs.push(`${tokens.fontHeading?.weight || 700} 16px "${headingFont}"`);
  if (bodyFont && bodyFont !== headingFont)
    specs.push(`${tokens.fontBody?.weight || 400} 16px "${bodyFont}"`);

  if (specs.length === 0) return true;
  return specs.every(s => document.fonts.check(s));
};

/**
 * Loads Google Fonts for the preview and prevents FOUT (flash of unstyled text)
 * by holding the previous font CSS variables until the new font is ready.
 *
 * This component renders AFTER CheckoutCSS in the DOM, so a :root style here
 * overrides CheckoutCSS's font variables at equal specificity.
 */
const PreviewFontLoader = ({ theme }) => {
  const currentFonts = useMemo(() => {
    if (!theme?.tokens) return null;
    return getFontFamilies(theme.tokens);
  }, [theme?.tokens]);

  const [loadedFonts, setLoadedFonts] = useState(currentFonts);
  const currentFontsRef = useRef(currentFonts);
  currentFontsRef.current = currentFonts;

  // Stable key that only changes when font families change, not weights
  const fontFamiliesKey = useMemo(() => {
    if (!theme) return '';
    try {
      const fontAnalysis = analyzeThemeFonts(theme);
      return fontAnalysis.fontFamilies.sort().join(',');
    } catch {
      return '';
    }
  }, [theme]);

  const fontUrl = useMemo(() => {
    if (!fontFamiliesKey || !theme) return null;
    try {
      const fontAnalysis = analyzeThemeFonts(theme);
      if (!fontAnalysis.hasCustomFonts) return null;
      const googleFonts = filterGoogleFonts(fontAnalysis.fonts);
      if (googleFonts.length === 0) return null;
      // Preload all weights so weight changes don't trigger a new CSS download
      const fontsWithAllWeights = googleFonts.map(font => ({
        ...font,
        weights: ALL_WEIGHTS,
      }));
      return generateGoogleFontsUrl(fontsWithAllWeights);
    } catch (error) {
      console.warn('Preview font loading failed:', error);
      return null;
    }
  }, [fontFamiliesKey]);

  // Before paint: if fonts are already cached (common when using the font
  // picker), update loadedFonts immediately so there's zero flash.
  useLayoutEffect(() => {
    if (currentFonts && areFontsLoaded(theme?.tokens)) {
      setLoadedFonts(currentFonts);
    }
  }, [fontUrl]);

  useEffect(() => {
    if (!fontUrl) {
      setLoadedFonts(currentFontsRef.current);
      return;
    }

    // Reuse an existing link for the same URL (another PreviewFontLoader
    // instance may have already created one, e.g. regular + fullscreen preview).
    const existing = document.querySelector(
      `link[data-preview-fonts][href="${CSS.escape(fontUrl)}"]`,
    );
    if (existing) {
      // Font stylesheet already in the DOM — just wait for fonts to be ready
      document.fonts.ready.then(() => setLoadedFonts(currentFontsRef.current));
      // Don't remove the shared link on unmount
      return;
    }

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = fontUrl;
    link.setAttribute('data-preview-fonts', 'true');
    link.crossOrigin = 'anonymous';

    // Safety timeout — always show the new font eventually
    const timeout = setTimeout(() => setLoadedFonts(currentFontsRef.current), 5000);

    const onComplete = () => {
      document.fonts.ready.then(() => {
        clearTimeout(timeout);
        // Only remove links for OLD fonts (different href), not duplicates
        // from other PreviewFontLoader instances using the same font.
        document.querySelectorAll('link[data-preview-fonts]').forEach(old => {
          if (old !== link && old.href !== link.href) old.remove();
        });
        setLoadedFonts(currentFontsRef.current);
      });
    };

    link.onload = onComplete;
    link.onerror = () => {
      clearTimeout(timeout);
      document.querySelectorAll('link[data-preview-fonts]').forEach(old => {
        if (old !== link && old.href !== link.href) old.remove();
      });
      setLoadedFonts(currentFontsRef.current);
    };

    document.head.appendChild(link);

    return () => {
      clearTimeout(timeout);
      // Only remove if no other PreviewFontLoader is using the same font
      const others = document.querySelectorAll(
        `link[data-preview-fonts][href="${CSS.escape(fontUrl)}"]`,
      );
      if (others.length <= 1) {
        link.remove();
      }
    };
  }, [fontUrl]);

  const needsOverride =
    loadedFonts &&
    currentFonts &&
    (loadedFonts.heading !== currentFonts.heading || loadedFonts.body !== currentFonts.body);

  if (!needsOverride) return null;

  // Override CheckoutCSS's font variables with the previously-loaded values.
  // This keeps the old font visible until the new one is ready.
  return (
    <style
      dangerouslySetInnerHTML={{
        __html: `:root { --cp_font-heading: ${loadedFonts.heading}; --cp_font-body: ${loadedFonts.body}; }`,
      }}
    />
  );
};

export default PreviewFontLoader;
